/**
 * @fileoverview Canonical Building Systems category registry — the seven
 *               Streamline families plus smoke detectors, shared between
 *               tenant-web and mobile-pwa (dry-package-first).
 * @module core-domain/entities/systemCategories
 *
 * Responsibilities:
 * - Declare the ordered category definitions (key / label / defaultName)
 *   every host uses to group `inspectableEntities.systems[]`.
 * - Provide `systemCategoryLabel` for display names.
 *
 * Dependencies: - None — pure data + a lookup function.
 * Architecture: - Single source; hosts re-export, never redefine.
 * Notes:            - Category keys are the persisted discriminator values;
 *   label/defaultName feed UI headers + new-record names.
 */

/**
 * @contract SystemCategoryDef
 * Purpose: One systems-family definition row.
 * Fields:  key (persisted discriminator), label (plural display),
 *   defaultName (new-record name).
 * Rules:   keys match the migrated `category` values; order is the UI
 *   display order.
 * Sync:    static data — identical on every host.
 */
export interface SystemCategoryDef {
  key: string;
  label: string;
  defaultName: string;
}

export const SYSTEM_CATEGORY_DEFS: SystemCategoryDef[] = [
  { key: 'backup_power', label: 'Backup Power', defaultName: 'Backup Power' },
  { key: 'building_systems', label: 'Building Systems', defaultName: 'Building System' },
  { key: 'department_access', label: 'Department Access', defaultName: 'Department Access' },
  { key: 'extinguishing_equipment', label: 'Extinguishing Equipment', defaultName: 'Extinguishing Equipment' },
  { key: 'extinguishing_systems', label: 'Extinguishing Systems', defaultName: 'Extinguishing System' },
  { key: 'fire_alarm', label: 'Fire Alarms', defaultName: 'Fire Alarm' },
  { key: 'fire_pump', label: 'Fire Pumps', defaultName: 'Fire Pump' },
  { key: 'smoke_detector', label: 'Detectors', defaultName: 'Smoke Detector' },
];

export const SYSTEM_CATEGORY_KEYS = SYSTEM_CATEGORY_DEFS.map((d) => d.key);

export function systemCategoryLabel(key: string): string {
  return SYSTEM_CATEGORY_DEFS.find((d) => d.key === key)?.label || key;
}

/**
 * Minimal shape for category matching — satisfied by EmbeddedBuildingSystem
 * and tenant-web's BuildingSystemSnapshot.
 */
interface CategoryMatchRecord {
  category?: string;
  sourceTable?: string;
  systemId?: string;
  _deleted?: boolean;
  [key: string]: unknown;
}

/**
 * @process systemsInCategory
 * Steps: return live records in `category`, tolerant of legacy aliases —
 *   direct key/label match, extinguishing-system aliases, normalized
 *   category/sourceTable, and `systemId` table-prefix match.
 * Inputs: embedded systems array (or undefined), category key.
 * Outputs: live records belonging to the family.
 * Failure Handling: absent arrays ⇒ [].
 * Idempotency: REQUIRED — pure filter.
 */
export function systemsInCategory<T extends CategoryMatchRecord>(
  systems: T[] | undefined,
  category: string,
): T[] {
  const catDef = SYSTEM_CATEGORY_DEFS.find((d) => d.key === category);
  const normalize = (cat?: unknown) =>
    typeof cat === 'string'
      ? cat.toLowerCase().trim().replace(/^system_/, '').replace(/[_\s-]+/g, '').replace(/s$/, '')
      : '';
  const targetNorm = normalize(category);

  return (systems || []).filter((s) => {
    if (!s) return false;
    // Tombstoned records stay embedded for merge integrity but are never
    // displayed as live systems (G-009).
    if (s._deleted === true) return false;
    // 1. Direct match on key or label or defaultName
    if (s.category === category) return true;
    if (catDef && (s.category === catDef.label || s.category === catDef.defaultName)) return true;

    // 2. Extinguishing systems aliases
    if (category === 'extinguishing_systems') {
      if (
        s.category === 'extinguishing_system' ||
        s.category === 'extinguishing' ||
        s.category === 'system_extinguishing' ||
        s.sourceTable === 'system_extinguishing' ||
        (typeof s.systemId === 'string' && (s.systemId.startsWith('system_extinguishing:') || s.systemId.startsWith('system_extinguishing_')))
      ) {
        return true;
      }
    }

    // 3. Normalized match on category
    if (s.category && normalize(s.category) === targetNorm) return true;

    // 4. Normalized match on sourceTable
    if (s.sourceTable && normalize(s.sourceTable) === targetNorm) return true;

    // 5. Match on systemId prefix if systemId is format table:id
    if (typeof s.systemId === 'string') {
      const parts = s.systemId.split(':');
      if (parts[0] && normalize(parts[0]) === targetNorm) return true;
    }

    return false;
  });
}
